import { useState, useRef, useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@/store'
import { useStudioStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import { ChevronDown, Building2, BarChart3, MapPin } from 'lucide-react'

interface GlobalAnalyticsSelectorProps {
  currentStudioId?: number
}

export function GlobalAnalyticsSelector({ currentStudioId }: GlobalAnalyticsSelectorProps = {}) {
  const [isOpen, setIsOpen] = useState(false)
  const [accessLevel, setAccessLevel] = useState<'standard' | 'advanced' | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { partnerNumber } = useParams<{ partnerNumber?: string }>()
  const { studios, selectedPartnerNumber, getPartnerStudios, setSelectedStudio } = useStudioStore()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )
  const activePartnerNumber = useMemo(() => {
    if (partnerNumber !== undefined) {
      const parsed = Number(partnerNumber)
      return Number.isNaN(parsed) ? null : parsed
    }
    return selectedPartnerNumber
  }, [partnerNumber, selectedPartnerNumber])
  const scopedStudios = useMemo(
    () => getPartnerStudios(activePartnerNumber),
    [getPartnerStudios, activePartnerNumber]
  )
  const isPartnerAnalyticsRoute =
    /^\/partners\/[^/]+\/analytics$/.test(location.pathname) ||
    location.pathname === '/analytics' ||
    location.pathname === '/management-analytics'
  const isStudioClientsRoute = /^\/partners\/[^/]+\/studios\/[^/]+\/clients$/.test(location.pathname)
  const isCityAnalyticsRoute = /^\/partners\/[^/]+\/cities\/\d+\/analytics$/.test(location.pathname)
  const cityIdFromPath = isCityAnalyticsRoute
    ? Number(location.pathname.match(/\/cities\/(\d+)\//)?.[1])
    : null

  useEffect(() => {
    const loadAccessLevel = async () => {
      if (!isManagementCompany || !user?.id) {
        setAccessLevel(null)
        return
      }
      const level = await getAccessLevel()
      setAccessLevel(level)
    }

    loadAccessLevel()
  }, [isManagementCompany, user?.id, getAccessLevel])

  const filteredStudios = useMemo(() => {
    if (!isManagementCompany || accessLevel !== 'advanced') {
      return scopedStudios
    }

    const visibleStatuses = new Set(['OPEN', 'PRE-OPEN', 'SERVICE'])
    return scopedStudios.filter((studio) => visibleStatuses.has(studio.status_code || 'OPEN'))
  }, [scopedStudios, isManagementCompany, accessLevel])

  const sortedStudios = useMemo(() => {
    return [...filteredStudios].sort((a, b) => {
      const cityA = (a.city?.city_name || '').trim()
      const cityB = (b.city?.city_name || '').trim()
      const byCity = cityA.localeCompare(cityB, 'ru', { sensitivity: 'base' })
      if (byCity !== 0) return byCity

      return String(a.studio_number).localeCompare(String(b.studio_number), 'ru', { numeric: true, sensitivity: 'base' })
    })
  }, [filteredStudios])

  // Group studios by city
  const cityGroups = useMemo(() => {
    const map = new Map<number, { cityName: string; studios: typeof sortedStudios }>()
    for (const studio of sortedStudios) {
      const cId = studio.city_id
      const cityName = studio.city?.city_name || 'Неизвестный город'
      if (!map.has(cId)) {
        map.set(cId, { cityName, studios: [] })
      }
      map.get(cId)!.studios.push(studio)
    }
    return Array.from(map.entries())
      .map(([cId, { cityName, studios }]) => ({ cityId: cId, cityName, studios }))
      .sort((a, b) => a.cityName.localeCompare(b.cityName, 'ru', { sensitivity: 'base' }))
  }, [sortedStudios])

  const hasMultipleCities = cityGroups.length > 1
  const currentCityGroup = cityIdFromPath != null
    ? cityGroups.find((g) => g.cityId === cityIdFromPath)
    : null

  // Закрываем dropdown при клике вне его
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleStudioChange = (studioId: number) => {
    const studio = scopedStudios.find(s => s.studio_internal_id === studioId)
    if (studio) {
      setSelectedStudio(studio)
    }
    const effectivePartnerNumber =
      activePartnerNumber ?? (studio?.partner_number != null ? Number(studio.partner_number) : null)

    if (effectivePartnerNumber !== null) {
      const segment = isStudioClientsRoute ? 'clients' : 'analytics'
      navigate(`/partners/${effectivePartnerNumber}/studios/${studioId}/${segment}`)
    } else {
      navigate('/partners')
    }
    setIsOpen(false)
  }

  const handleCityChange = (cityId: number) => {
    if (activePartnerNumber !== null) {
      navigate(`/partners/${activePartnerNumber}/cities/${cityId}/analytics`)
    }
    setIsOpen(false)
  }

  const handleAllStudiosChange = () => {
    if (activePartnerNumber !== null) {
      navigate(`/partners/${activePartnerNumber}/analytics`)
    }
    setIsOpen(false)
  }

  const currentStudio = currentStudioId != null
    ? scopedStudios.find(s => s.studio_internal_id === currentStudioId)
    : null

  const buttonLabel = isCityAnalyticsRoute && currentCityGroup
    ? `Свод по ${currentCityGroup.cityName} (${currentCityGroup.studios.length})`
    : isPartnerAnalyticsRoute
      ? `Свод по студиям франчайзи (${sortedStudios.length})`
      : currentStudio
        ? `${currentStudio.city?.city_name || 'Студия'} №${currentStudio.studio_number}`
        : `Свод по всем студиям (${sortedStudios.length})`

  if (sortedStudios.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Нет доступных студий
      </p>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 group"
      >
        <span>{buttonLabel}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform group-hover:text-foreground ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-auto min-w-[280px] bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
          <div className="max-h-[400px] overflow-y-auto">
            {hasMultipleCities ? (
              <>
                {cityGroups.map((group, index) => {
                  const isCityActive = cityIdFromPath === group.cityId
                  return (
                    <div key={group.cityId}>
                      {index > 0 && <div className="border-t border-border/50" />}

                      {/* City aggregate row — only when city has 2+ studios */}
                      {group.studios.length > 1 && (
                        <button
                          onClick={() => handleCityChange(group.cityId)}
                          className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${
                            isCityActive ? 'bg-primary/10' : 'hover:bg-muted/50'
                          }`}
                        >
                          <MapPin className={`w-4 h-4 flex-shrink-0 ${isCityActive ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${isCityActive ? 'text-primary' : 'text-foreground'}`}>
                              {group.cityName} — свод ({group.studios.length} студи{group.studios.length < 5 ? 'и' : 'й'})
                            </div>
                          </div>
                          {isCityActive && <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                        </button>
                      )}

                      {/* Studios in city */}
                      {group.studios.map((studio) => {
                        const isStudioActive = currentStudioId === studio.studio_internal_id
                        return (
                          <button
                            key={studio.studio_internal_id}
                            onClick={() => handleStudioChange(studio.studio_internal_id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${isStudioActive ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                          >
                            <Building2 className={`w-4 h-4 flex-shrink-0 ${isStudioActive ? 'text-primary' : 'text-muted-foreground'}`} />
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium truncate ${isStudioActive ? 'text-primary' : 'text-foreground'}`}>
                                {studio.city?.city_name || 'Студия'} №{studio.studio_number}
                              </div>
                              {studio.address && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {studio.address}
                                </div>
                              )}
                            </div>
                            {isStudioActive && <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}

                {/* All studios aggregate at bottom */}
                <div className="border-t border-border">
                  <button
                    onClick={handleAllStudiosChange}
                    className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${
                      isPartnerAnalyticsRoute ? 'bg-primary/10' : 'hover:bg-muted/50'
                    }`}
                  >
                    <BarChart3 className={`w-4 h-4 flex-shrink-0 ${isPartnerAnalyticsRoute ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${isPartnerAnalyticsRoute ? 'text-primary' : 'text-foreground'}`}>
                        Свод по всем студиям ({sortedStudios.length})
                      </div>
                    </div>
                    {isPartnerAnalyticsRoute && <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Single city — flat list */}
                {sortedStudios.map((studio) => {
                  const isStudioActive = currentStudioId === studio.studio_internal_id
                  return (
                    <button
                      key={studio.studio_internal_id}
                      onClick={() => handleStudioChange(studio.studio_internal_id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${isStudioActive ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                    >
                      <Building2 className={`w-4 h-4 flex-shrink-0 ${isStudioActive ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${isStudioActive ? 'text-primary' : 'text-foreground'}`}>
                          {studio.city?.city_name || 'Студия'} №{studio.studio_number}
                        </div>
                        {studio.address && (
                          <div className="text-xs text-muted-foreground truncate">
                            {studio.address}
                          </div>
                        )}
                      </div>
                      {isStudioActive && <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                    </button>
                  )
                })}

                {/* Aggregate footer */}
                <div className="border-t border-border">
                  <button
                    onClick={handleAllStudiosChange}
                    className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${
                      isPartnerAnalyticsRoute ? 'bg-primary/10' : 'hover:bg-muted/50'
                    }`}
                  >
                    <BarChart3 className={`w-4 h-4 flex-shrink-0 ${isPartnerAnalyticsRoute ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${isPartnerAnalyticsRoute ? 'text-primary' : 'text-foreground'}`}>
                        Сводная аналитика
                      </div>
                      <div className="text-xs text-muted-foreground">
                        По всем студиям франчайзи
                      </div>
                    </div>
                    {isPartnerAnalyticsRoute && <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
